/**
 * Раздел заказа по экологии в карточке заказа у команды (ecology-order): заявка клиента (услуга,
 * площадки, ответы, комментарий) и результаты по каждой площадке — дата результата (обязательна для
 * завершения), срок действия документа (если отличается от срока по справочнику) и готовый документ
 * или квитанция (необязательно).
 *
 * Результаты вносятся у заказа «В работе»; после завершения их правит только администратор (сервер
 * проверяет авторитетно). Изменение результата у завершённого заказа сразу обновляет строку раздела
 * «Экология» у клиента.
 */
import { useRef, useState } from 'react'
import { FileUp, Paperclip, X } from 'lucide-react'
import { teamEcologyOrdersApi, type EcoOrder, type OrderDataStatus } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_KIND_SHORT_LABELS, ecoTermLabel } from '@/lib/ecology'
import { EcoOrderError, EcoOrderSummary, useEcoOrder } from './order-summary'
import { Card } from '@/design-system/components/layout/card'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { Alert } from '@/design-system/components/feedback/alert'

/** Типы файлов результата — как на сервере (files.DocsImagesExt). */
const FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'

interface Draft {
  resultDate: string
  validUntil: string
}

export function TeamEcologySection({
  orderId,
  status,
  onResultsSaved,
}: {
  orderId: string
  status: OrderDataStatus
  /** Результаты сохранены — карточке заказа нужно обновить доступность «Завершить». */
  onResultsSaved?: () => void
}) {
  const { ecoOrder, error, setEcoOrder } = useEcoOrder((signal) =>
    teamEcologyOrdersApi.get(orderId, signal),
  )
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [saving, setSaving] = useState(false)
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const uploadFor = useRef<string | null>(null)

  if (error) return <EcoOrderError error={error} />
  if (!ecoOrder)
    return (
      <Panel>
        <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
      </Panel>
    )

  const canEditResults = status === 'in_progress' || status === 'done'
  const isDocument = ecoOrder.item?.kind === 'document'
  // Поля карточки позиции (ecology-card-fields): у постановки на учёт «Действует до» удалено, а
  // дата подписана по-своему — результат заказа вносится теми же полями, что и строка раздела.
  const hasValidUntil = isDocument && !ecoOrder.item?.hiddenFields.includes('valid_until')
  const dateLabel = isDocument
    ? ecoOrder.item?.fieldLabels.developed_on || 'Дата разработки'
    : 'Дата сдачи'
  const draftOf = (id: string): Draft => {
    const site = ecoOrder.sites.find((s) => s.id === id)
    return (
      drafts[id] ?? { resultDate: site?.resultDate ?? '', validUntil: site?.resultValidUntil ?? '' }
    )
  }
  const patchDraft = (id: string, next: Partial<Draft>) => {
    setSaved(false)
    setDrafts((prev) => ({ ...prev, [id]: { ...draftOf(id), ...next } }))
  }

  async function apply(action: () => Promise<EcoOrder>, fallback: string, rowID?: string) {
    setActionError(null)
    setBusyRow(rowID ?? null)
    try {
      const next = await action()
      setEcoOrder(next)
      setDrafts({})
      onResultsSaved?.()
      return true
    } catch (err) {
      setActionError(getErrorMessage(err, fallback))
      return false
    } finally {
      setBusyRow(null)
    }
  }

  async function saveResults() {
    if (!ecoOrder || saving) return
    setSaving(true)
    const ok = await apply(
      () =>
        teamEcologyOrdersApi.putResults(
          orderId,
          ecoOrder.sites.map((site) => ({
            id: site.id,
            resultDate: draftOf(site.id).resultDate,
            validUntil: hasValidUntil ? draftOf(site.id).validUntil : '',
          })),
        ),
      'Не удалось сохранить результаты',
    )
    setSaved(ok)
    setSaving(false)
  }

  return (
    <div className="flex flex-col gap-4">
      <EcoOrderSummary ecoOrder={ecoOrder} />

      <Card
        header={
          <span className="flex flex-wrap items-center justify-between gap-2">
            Результат по площадкам
            {ecoOrder.item ? (
              <span className="text-12 font-normal text-txt-tertiary">
                {ECO_KIND_SHORT_LABELS[ecoOrder.item.kind]} · {ecoTermLabel(ecoOrder.item)}
              </span>
            ) : null}
          </span>
        }
      >
        <p className="mb-3 text-13 text-txt-secondary">
          {hasValidUntil
            ? 'Дата разработки обязательна для завершения заказа; срок действия — если он отличается от срока по справочнику. Файл документа необязателен.'
            : isDocument
              ? `${dateLabel} — обязательна для завершения заказа. Файл документа необязателен.`
              : 'Дата сдачи обязательна для завершения заказа. Квитанция или отчёт — необязательно.'}
        </p>
        {actionError ? (
          <Alert variant="danger" className="mb-3">
            {actionError}
          </Alert>
        ) : null}
        {!canEditResults ? (
          <Alert variant="info" className="mb-3">
            Результаты вносятся, когда заказ принят в работу.
          </Alert>
        ) : null}

        <div className="flex flex-col gap-4">
          {ecoOrder.sites.map((site) => (
            <div key={site.id} className="rounded-10 border border-line p-3">
              <div className="font-medium text-txt">{site.name}</div>
              {site.address ? (
                <div className="mb-2 text-12 text-txt-secondary">{site.address}</div>
              ) : null}
              <div className="grid grid-cols-1 gap-x-4 nw:grid-cols-2">
                <Field label={dateLabel} className="my-2">
                  {(props) => (
                    <DateInput
                      {...props}
                      value={draftOf(site.id).resultDate}
                      disabled={!canEditResults}
                      onChange={(resultDate) => patchDraft(site.id, { resultDate })}
                    />
                  )}
                </Field>
                {hasValidUntil ? (
                  <Field
                    label="Действует до"
                    hint="Если отличается от срока по справочнику"
                    className="my-2"
                  >
                    {(props) => (
                      <DateInput
                        {...props}
                        value={draftOf(site.id).validUntil}
                        disabled={!canEditResults}
                        onChange={(validUntil) => patchDraft(site.id, { validUntil })}
                      />
                    )}
                  </Field>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {site.resultFileUrl ? (
                  <a
                    href={site.resultFileUrl}
                    className="inline-flex max-w-64 items-center gap-1 text-13 text-accent hover:underline"
                    title={site.resultFileName}
                  >
                    <Paperclip className="size-4 shrink-0 stroke-current" aria-hidden />
                    <span className="truncate">{site.resultFileName}</span>
                  </a>
                ) : (
                  <span className="text-13 text-txt-tertiary">файла нет</span>
                )}
                {canEditResults ? (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyRow === site.id}
                      leftIcon={<FileUp className="size-4 stroke-current" aria-hidden />}
                      onClick={() => {
                        uploadFor.current = site.id
                        fileRef.current?.click()
                      }}
                    >
                      {site.resultFileUrl ? 'Заменить файл' : 'Загрузить файл'}
                    </Button>
                    {site.resultFileUrl ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        title="Убрать файл"
                        aria-label={`Убрать файл площадки «${site.name}»`}
                        disabled={busyRow === site.id}
                        onClick={() =>
                          void apply(
                            () => teamEcologyOrdersApi.deleteFile(orderId, site.id),
                            'Не удалось убрать файл',
                            site.id,
                          )
                        }
                      >
                        <X className="size-4 stroke-current" aria-hidden />
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {canEditResults ? (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button disabled={saving} onClick={() => void saveResults()}>
              {saving ? 'Сохранение…' : 'Сохранить результаты'}
            </Button>
            <span aria-live="polite" className="text-13 text-txt-secondary">
              {saved
                ? 'Сохранено'
                : ecoOrder.resultsReady
                  ? ''
                  : 'Заполните даты по всем площадкам'}
            </span>
          </div>
        ) : null}
      </Card>

      <input
        ref={fileRef}
        type="file"
        accept={FILE_ACCEPT}
        className="hidden"
        aria-label="Файл результата"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          const rowID = uploadFor.current
          uploadFor.current = null
          if (file && rowID)
            void apply(
              () => teamEcologyOrdersApi.uploadFile(orderId, rowID, file),
              'Не удалось загрузить файл',
              rowID,
            )
        }}
      />
    </div>
  )
}
